import React, { useState, useEffect } from "react"

import { Button, Dropdown, DropdownButton, ButtonGroup } from "react-bootstrap"
import { FaPlus, FaEllipsisV, FaCheck, FaChevronRight } from "react-icons/fa"
import { Link } from "react-router-dom"
import CreateCheckin from "./CreateCheckin"
import { MDBDataTable } from "mdbreact"
import axios from "axios"
import { localserver } from "../../../api/common"
import { showToast, ToastMessage } from "../../Shared/ToastMessage"
import PreviewCheckinModal from "./PreviewCheckin"
import { useUser } from "../../AdminUsers/UserContext"

const AttendeesCheckin = () => {
  const { checkPermission } = useUser()
  const [activeTab, setActiveTab] = useState("All")
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [attendeesCheckin, setAttendeesCheckin] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [checkInToEdit, setCheckInToEdit] = useState(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  const fetchAttendeesCheckin = async (filters = {}) => {
    setIsLoading(true)
    setError(null)
    const storedEventId = localStorage.getItem("eventId")
    if (!storedEventId) {
      setError("No event ID found. Please select an event.")
      showToast("No event ID found. Please select an event.", "error")
      setIsLoading(false)
      return
    }

    try {
      const response = await axios.post(
        `${localserver}/checkin_list`,
        {
          event_id: localStorage.getItem("eventId"),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )

      if (response.data.success && Array.isArray(response.data.data)) {
        setAttendeesCheckin(response.data.data)
      }
    } catch (error) {
      console.error("Error fetching Check-in:", error)
      setError("Failed to fetch Check-in. Please try again.")
      showToast("Failed to fetch Check-in. Please try again.", "error")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchAttendeesCheckin()
  }, [])

  const handleTabChange = (tab) => {
    setActiveTab(tab)
  }

  const handleCreateCheckIn = (newCheckIn) => {
    fetchAttendeesCheckin()
    setShowCreateModal(false)
    setCheckInToEdit(null)
  }

  const handleEditCheckIn = async (checkin) => {
    const hasPermission = await checkPermission("Check-in", "update")
    if (!hasPermission) return
    setCheckInToEdit(checkin)
    setShowCreateModal(true)
  }

  const handlePreviewCheckIn = (checkin) => {
    setCheckInToEdit(checkin)
    setShowPreviewModal(true)
  }

  const handleToggleStatus = async (id, value, checkin) => {
    let checkInStatus = value ? "onsale" : "paused"
    try {
      const response = await axios.post(
        `${localserver}/checkin_master_status_update`,
        {
          id: id,
          user_id: localStorage.getItem("loginUserId"),
          event_id: localStorage.getItem("eventId"),
          status: checkInStatus,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )

      if (response.data.success) {
        const updatedCheckin = { ...checkin, status: value, checkin_status: checkInStatus }
        setAttendeesCheckin(
          attendeesCheckin.map((c) => (c.id === id ? updatedCheckin : c)),
        )
        showToast(response.data.message, "success")
        await fetchAttendeesCheckin()
        setCheckInToEdit(attendeesCheckin.find(c => c.id === id) || updatedCheckin)
        setShowPreviewModal(true)
      } else {
        showToast(response.data.message || "Failed to update check-in status.", "error")
      }
    } catch (error) {
      console.error("Error updating check-in status:", error)
      showToast("Failed to update check-in status. Please try again.", "error")
    }
  }

  const handleDeleteCheckin = async (id) => {
    const hasPermission = await checkPermission("Check-in", "delete")
    if (!hasPermission) return
    try {
      const response = await axios.post(
        `${localserver}/checkin_master_delete`,
        {
          id: id,
          event_id: localStorage.getItem("eventId"),
          user_id: localStorage.getItem("loginUserId"),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )

      if (response.data.success) {
        showToast(response.data.message, "success")
        fetchAttendeesCheckin()
      } else {
        showToast(response.data.message || "Failed to update check-in status.", "error")
      }
    } catch (error) {
      console.error("Error updating check-in status:", error)
      showToast("Failed to update check-in status. Please try again.", "error")
    }
  }

  const filteredCheckins = attendeesCheckin.filter((checkin) => {
    if (activeTab === "All") return true
    if (activeTab === "On Sale") return checkin.status === true
    if (activeTab === "Paused") return checkin.status === false
    if (activeTab === "Drafts") return checkin.status === "draft"
    if (activeTab === "Archived") return checkin.status === "archived"
    if (activeTab === "Deleted") return checkin.status === "deleted"
    return true
  })

  const getStatusBadge = (checkin) => {
    return (
      <label className="switch">
        <input
          type="checkbox"
          className="lv-btn small-switch"
          checked={checkin.checkin_status === "onsale"}
          onChange={(e) => { e.stopPropagation(); handleToggleStatus(checkin.id, e.target.checked, checkin); }}
        />
        <span className="slider round"></span>
      </label>
    )
  }

  const handleCreateCheckInopen = async () => {
    const hasPermission = await checkPermission("Check-in", "create")
    if (!hasPermission) return
    setCheckInToEdit(null)
    setShowCreateModal(true)
  }

  const [checkUserLoading, setCheckUserLoading] = useState(false)
  const fetchPermission = async () => {
    setCheckUserLoading(true)
    try {
      const hasPermission = await checkPermission("Check-in", "view")
      if (!hasPermission) return
    } catch (error) {
      console.error("Error Check Permission:", error)
    } finally {
      setCheckUserLoading(false)
    }
  }

  useEffect(() => {
    fetchPermission()
  }, [])

  const tableData = {
    columns: [
      { label: "Check-in Name", field: "checkin_name", sort: "asc" },
      { label: "Ticket Name", field: "ticket_names", sort: "asc" },
      { label: "Entry Type", field: "entry_type", sort: "asc" },
      { label: "Status", field: "status", sort: "asc" },
      { label: "Actions", field: "actions", sort: "disabled", width: 40 },
    ],
    rows: filteredCheckins.map((checkin) => ({
      checkin_name: (
        <div className="d-flex align-items-center gap-7">
          <div>
            <div className="check_in_content">{checkin.checkin_name}</div>
            <div className="check_in_content small">
              {checkin.ticket_ids ? checkin.ticket_ids.split(",").length : 0} tickets
              <Button
                onClick={() => handlePreviewCheckIn(checkin)}
                variant="link"
                size="sm"
                className="ml-2 custom_button_out btn btn-outline-default"
              >
                Show
              </Button>
            </div>
          </div>
        </div>
      ),
      ticket_names: checkin.ticket_names,
      entry_type: `${checkin.entry_type.charAt(0).toUpperCase()}${checkin.entry_type.slice(1)}`,
      status: getStatusBadge(checkin),      
      actions: (
        <Dropdown as={ButtonGroup} key={`dropdown-${checkin.id}`} onClick={(e) => e.stopPropagation()}>
          <DropdownButton variant="link" id={`dropdown-actions-${checkin.id}`} title={<FaEllipsisV />} size="sm">
            <Dropdown.Item onClick={() => handleEditCheckIn(checkin)}>Edit</Dropdown.Item>
            <Dropdown.Item onClick={() => handleDeleteCheckin(checkin.id)}>Delete</Dropdown.Item>
          </DropdownButton>
        </Dropdown>
      ),
      clickEvent: (e) => {
        if (
          e.target.closest(".dropdown") ||
          e.target.closest(".dropdown-toggle") ||
          e.target.closest(".dropdown-menu") ||
          e.target.closest(".toggle-guest")
        ) {
          return
        }
        handlePreviewCheckIn(checkin)
      }
    })),
  }

  return (
    <>
      {checkUserLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid #e5e7eb",
              borderTop: "4px solid #184778",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <span style={{ color: "#6b7280", fontSize: "16px" }}>Loading check-in...</span>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      ) : (
        <div style={{ minHeight: "100vh", backgroundColor: "#f9fafb", padding: "24px 15px" }}>
          <div  >
            <ToastMessage />

            {/* Header Section with Consistent Breadcrumbs */}
            <div style={{ marginBottom: "22px" }}>
              <div className="brood_comes_div">
                <div className="row align-items-center">
                  <div className="col-md-6 brood_comes_heading">
                    <h2>
                      <div className="brood_comes_part">
                        <FaCheck  /></div>
                      Check-in
                    </h2>
                    {/* Consistent Breadcrumbs Pattern */}
                    {/* <nav aria-label="breadcrumb">
                      <ol
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          listStyle: "none",
                          padding: 0,
                          margin: 0,
                          fontSize: "14px",
                        }}
                      >
                        <li>
                          <Link
                            to="/events/admin/"
                            style={{
                              color: "#6b7280",
                              textDecoration: "none",
                              transition: "color 0.2s ease",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = "#184778")}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "#6b7280")}
                          >
                            AMASI
                          </Link>
                        </li>
                        <li style={{ color: "#d1d5db" }}>
                          <FaChevronRight size={10} />
                        </li>
                        <li style={{ color: "#184778", fontWeight: "500" }}>Check-in</li>
                      </ol>
                    </nav> */}
                  </div>
                  <div className="col-md-6 text-end">
                    <button
                      onClick={() => handleCreateCheckInopen()}
                      className="btn btn-outline-primary "
                    >
                      <FaPlus size={16} />
                      Create Check-in
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Table Section */}
            <div
              style={{
                background: "#ffffff",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: "1px solid #e5e7eb",
                overflow: "hidden",
              }}
            >
              {error && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    padding: "16px",
                    margin: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <i
                    className="fa fa-exclamation-triangle"
                    style={{
                      color: "#dc2626",
                      fontSize: "20px",
                    }}
                  ></i>
                  <p style={{ color: "#dc2626", margin: 0 }}>{error}</p>
                </div>
              )}

              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "40px",
                    color: "#6b7280",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      border: "3px solid #e5e7eb",
                      borderTop: "3px solid #184778",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      marginRight: "12px",
                    }}
                  ></div>
                  <span>Loading Check-in...</span>
                </div>
              ) : (
                <>
                  <div style={{ padding: "32px" }}>
                    <MDBDataTable
                      noBottomColumns
                      className="table table-hover js-basic-example dataTable table-custom spacing5"
                      entriesOptions={[5, 10, 20, 25]}
                      entries={10}
                      paging
                      pagesAmount={5}
                      data={tableData}
                      sortRows={["checkin_name"]}
                      preserveScroll={true}
                      preserveState={true}
                    />
                  </div>
                  <div
                    style={{
                      padding: "16px 20px",
                      borderTop: "1px solid #e5e7eb",
                      background: "#f9fafb",
                      fontSize: "14px",
                      color: "#6b7280",
                    }}
                  >
                    Showing 1 to {filteredCheckins.length} records of {attendeesCheckin.length}
                  </div>
                </>
              )}
            </div>
          </div>

          <CreateCheckin
            show={showCreateModal}
            onHide={() => {
              setShowCreateModal(false)
              setCheckInToEdit(null)
            }}
            onSubmit={handleCreateCheckIn}
            checkInToEdit={checkInToEdit}
          />
          <PreviewCheckinModal
            show={showPreviewModal}
            onHide={() => setShowPreviewModal(false)}
            checkinId={checkInToEdit && checkInToEdit.id}
          />
        </div>
      )}
    </>
  )
}

export default AttendeesCheckin